import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Pencil, X, Check, Upload, Wand2, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptySeries = {
  name: '', slug: '', location: '', description: '',
  start_date: '', end_date: '',
  poster_url: '', hero_url: '',
  is_featured: false,
};

const STATUS_PILL = {
  opened: { label: 'Happening Now', cls: 'bg-emerald-600/20 text-emerald-300 border-emerald-500/30' },
  upcoming: { label: 'Upcoming', cls: 'bg-secondary-main/20 text-secondary-main border-secondary-main/30' },
  archived: { label: 'Archived', cls: 'bg-white/[0.05] text-white/50 border-white/[0.08]' },
};

export const SeriesTab = ({ token }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptySeries });
  const [saving, setSaving] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);
  const [autoDetecting, setAutoDetecting] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/series`, { headers });
    setItems(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing('new'); setForm({ ...emptySeries }); };
  const openEdit = (s) => { setEditing(s.id); setForm({ ...emptySeries, ...s }); };
  const closeForm = () => { setEditing(null); setForm({ ...emptySeries }); };

  const save = async () => {
    if (!form.name || !form.start_date || !form.end_date) {
      alert('Name, start date and end date are required');
      return;
    }
    setSaving(true);
    try {
      if (editing === 'new') {
        await axios.post(`${API}/admin/series`, form, { headers });
      } else {
        await axios.put(`${API}/admin/series/${editing}`, form, { headers });
      }
      await load();
      closeForm();
    } catch (e) {
      alert('Save failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this series? This cannot be undone.')) return;
    await axios.delete(`${API}/admin/series/${id}`, { headers });
    await load();
  };

  const autoDetect = async () => {
    if (!window.confirm(
      'Auto-detect series from the synced tournament calendar?\n\n' +
      'This will scan your tournaments and create a new Series for every cluster of dates ' +
      '(gaps over 3 days start a new festival). Existing series are kept untouched.'
    )) return;
    setAutoDetecting(true);
    try {
      const { data } = await axios.post(`${API}/admin/series/auto-detect`, {}, { headers });
      await load();
      const created = data.created || 0;
      const updated = data.updated || 0;
      if (created === 0 && updated === 0) {
        alert(data.message || 'Already up-to-date — no new or changed series.');
      } else {
        const parts = [];
        if (created) parts.push(`${created} new series`);
        if (updated) parts.push(`${updated} expanded to cover full league range`);
        alert('✓ ' + parts.join(' · '));
      }
    } catch (e) {
      alert('Auto-detect failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setAutoDetecting(false);
    }
  };

  const uploadImage = async (field) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploadingField(field);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await axios.post(`${API}/admin/upload`, fd, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
        const url = data.url || `${API}/files/${data.path}`;
        setForm(f => ({ ...f, [field]: url }));
      } catch (err) {
        alert('Upload failed');
      } finally {
        setUploadingField(null);
      }
    };
    input.click();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold text-white">Series &amp; Festivals</h2>
          <p className="font-body text-xs text-white/40 mt-1">
            Curated tour stops shown on the public Home and /series pages. Status (Opened / Upcoming / Archived) is auto-computed from the date range.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="series-auto-detect-btn"
            onClick={autoDetect}
            disabled={autoDetecting}
            variant="outline"
            className="border-secondary-main/40 text-secondary-main hover:bg-secondary-main/10 hover:text-secondary-main font-heading tracking-wider"
          >
            {autoDetecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
            {autoDetecting ? 'Detecting…' : 'Auto-Detect from Tournaments'}
          </Button>
          <Button data-testid="series-add-btn" onClick={openCreate} className="bg-primary-main text-black hover:bg-primary-hover font-heading tracking-wider">
            <Plus className="w-4 h-4 mr-2" /> Add Series
          </Button>
        </div>
      </div>

      {/* Form */}
      {editing && (
        <div className="mb-6 rounded-2xl border border-primary-main/30 bg-background-surface p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-bold text-white">{editing === 'new' ? 'New Series' : 'Edit Series'}</h3>
            <button onClick={closeForm} className="text-white/40 hover:text-white" data-testid="series-form-close"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name" required>
              <Input data-testid="series-field-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="RDPT+ APRIL 2026" className="bg-background-base border-white/10 text-white" />
            </Field>
            <Field label="Location">
              <Input data-testid="series-field-location" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Manila, Philippines" className="bg-background-base border-white/10 text-white" />
            </Field>
            <Field label="Start Date" required>
              <Input data-testid="series-field-start-date" type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} className="bg-background-base border-white/10 text-white" />
            </Field>
            <Field label="End Date" required>
              <Input data-testid="series-field-end-date" type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} className="bg-background-base border-white/10 text-white" />
            </Field>
            <Field label="Slug (URL)">
              <Input data-testid="series-field-slug" value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="auto-generated from name" className="bg-background-base border-white/10 text-white" />
            </Field>
            <div className="flex items-end gap-3">
              <div className="flex items-center gap-2">
                <Switch data-testid="series-field-featured" checked={!!form.is_featured} onCheckedChange={v => setForm({ ...form, is_featured: v })} />
                <Label className="text-white/70 text-sm">Featured</Label>
              </div>
            </div>
            <div className="md:col-span-2">
              <Field label="Description">
                <textarea
                  data-testid="series-field-description"
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  placeholder="Short pitch shown on cards & detail page"
                  className="w-full rounded-md border border-white/10 bg-background-base text-white/90 font-body text-sm p-3 focus:outline-none focus:ring-1 focus:ring-primary-main/60"
                />
              </Field>
            </div>

            {/* Image uploads */}
            <ImageField label="Poster (card banner — wide image looks best)" field="poster_url" form={form} setForm={setForm} uploadingField={uploadingField} uploadImage={uploadImage} />
            <ImageField label="Hero Background (detail page banner)" field="hero_url" form={form} setForm={setForm} uploadingField={uploadingField} uploadImage={uploadImage} />
          </div>

          <div className="mt-5 flex gap-3">
            <Button data-testid="series-form-save" onClick={save} disabled={saving} className="bg-secondary-main text-white hover:bg-secondary-main/90 font-heading tracking-wider">
              <Check className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button onClick={closeForm} variant="ghost" className="text-white/60 hover:text-white">Cancel</Button>
          </div>
        </div>
      )}

      {/* List */}
      {items.length === 0 ? (
        <div className="text-center py-16 border border-white/[0.06] rounded-2xl">
          <p className="font-body text-sm text-white/40">No series yet. Click "Add Series" to create your first one.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(s => (
            <div key={s.id} data-testid={`series-row-${s.id}`} className="flex items-center gap-4 p-4 rounded-xl border border-white/[0.06] bg-white/[0.02]">
              <div className="flex-shrink-0 w-20 h-16 rounded-md overflow-hidden bg-black/40">
                {s.poster_url ? <img src={s.poster_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-primary-main/15 to-secondary-main/10" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading text-base font-bold text-white truncate">{s.name}</h3>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-heading uppercase tracking-[0.2em] border ${STATUS_PILL[s.status]?.cls || ''}`}>
                    {STATUS_PILL[s.status]?.label || s.status}
                  </span>
                </div>
                <p className="font-mono text-xs text-white/50 mt-1">{s.start_date} → {s.end_date} {s.location && `• ${s.location}`}</p>
              </div>
              <Button data-testid={`series-edit-${s.id}`} onClick={() => openEdit(s)} variant="ghost" className="text-white/60 hover:text-white"><Pencil className="w-4 h-4" /></Button>
              <Button data-testid={`series-delete-${s.id}`} onClick={() => remove(s.id)} variant="ghost" className="text-red-400/70 hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <Label className="font-heading text-[10px] tracking-[0.2em] uppercase text-white/50">
      {label}{required && <span className="text-primary-main ml-0.5">*</span>}
    </Label>
    <div className="mt-2">{children}</div>
  </div>
);

const ImageField = ({ label, field, form, setForm, uploadingField, uploadImage }) => (
  <Field label={label}>
    <div className="flex items-center gap-2">
      <Input
        data-testid={`series-field-${field}`}
        value={form[field] || ''}
        onChange={e => setForm({ ...form, [field]: e.target.value })}
        placeholder="Paste URL or upload"
        className="bg-background-base border-white/10 text-white flex-1"
      />
      <Button onClick={() => uploadImage(field)} disabled={uploadingField === field} variant="ghost" className="border border-white/10 text-white/70 hover:text-white" data-testid={`series-upload-${field}`}>
        <Upload className="w-4 h-4 mr-1.5" /> {uploadingField === field ? 'Uploading…' : 'Upload'}
      </Button>
    </div>
    {form[field] && <img src={form[field]} alt="" className="mt-2 max-h-24 rounded-md border border-white/10" />}
  </Field>
);
